// CSP Debug Utility for Production Debugging with Google Analytics Support
class CSPDebugger {
  constructor() {
    this.violations = [];
    this.apiTests = [];
    this.googleAnalyticsErrors = [];
    this.isProduction = process.env.NODE_ENV === 'production';
    this.gtagLoadAttempts = 0;
    this.maxGtagRetries = 5;
    
    this.setupCSPViolationListener();
    this.setupConsoleOverride();
    this.setupGoogleAnalyticsMonitoring();
  }

  setupCSPViolationListener() {
    document.addEventListener('securitypolicyviolation', (e) => {
      const violation = {
        timestamp: new Date().toISOString(),
        blockedURI: e.blockedURI,
        violatedDirective: e.violatedDirective,
        effectiveDirective: e.effectiveDirective,
        originalPolicy: e.originalPolicy,
        disposition: e.disposition,
        statusCode: e.statusCode,
        sourceFile: e.sourceFile,
        lineNumber: e.lineNumber,
        columnNumber: e.columnNumber,
        isGoogleAnalytics: this.isGoogleAnalyticsRelated(e.blockedURI)
      };
      
      this.violations.push(violation);
      
      // Special handling for Google Analytics violations
      if (violation.isGoogleAnalytics) {
        this.handleGoogleAnalyticsViolation(violation);
      }
      
      // Log to console for debugging
      console.group('🚫 CSP Violation Detected');
      console.error('Blocked URI:', e.blockedURI);
      console.error('Violated Directive:', e.violatedDirective);
      console.error('Effective Directive:', e.effectiveDirective);
      if (violation.isGoogleAnalytics) {
        console.error('⚠️ Google Analytics Related:', true);
        console.error('💡 Suggested Fix: Update CSP to include Google Analytics domains');
      }
      console.error('Full violation:', violation);
      console.groupEnd();
      
      // Store in localStorage for debugging
      this.saveViolationsToStorage();
    });
  }

  isGoogleAnalyticsRelated(uri) {
    const gaPatterns = [
      'googletagmanager.com',
      'google-analytics.com',
      'analytics.google.com',
      'gtag'
    ];
    return gaPatterns.some(pattern => uri.includes(pattern));
  }

  handleGoogleAnalyticsViolation(violation) {
    this.googleAnalyticsErrors.push({
      ...violation,
      suggestedFix: this.getGoogleAnalyticsFix(violation)
    });
    
    // Try to implement fallback tracking
    this.implementGoogleAnalyticsFallback();
    
    console.warn('🔧 Google Analytics CSP Violation - Attempting Fallback');
  }

  getGoogleAnalyticsFix(violation) {
    const fixes = {
      'script-src': 'Add https://www.googletagmanager.com to script-src directive',
      'script-src-elem': 'Add https://www.googletagmanager.com to script-src-elem directive',
      'connect-src': 'Add https://www.google-analytics.com and https://www.googletagmanager.com to connect-src',
      'img-src': 'Add https://www.google-analytics.com to img-src directive'
    };
    
    return fixes[violation.violatedDirective] || 'Review CSP policy for Google Analytics compatibility';
  }

  implementGoogleAnalyticsFallback() {
    if (this.gtagLoadAttempts >= this.maxGtagRetries) {
      console.warn('🚫 Google Analytics fallback attempts exhausted');
      return;
    }
    
    this.gtagLoadAttempts++;
    
    try {
      // Try to manually initialize gtag if window.gtag exists
      if (window.gtag) {
        console.log('✅ Google Analytics gtag found, attempting manual initialization');
        window.gtag('config', 'G-FQWNYFDSCV', {
          page_title: document.title,
          page_location: window.location.href,
          send_page_view: true,
          debug_mode: true
        });
      } else {
        // Schedule retry
        setTimeout(() => this.implementGoogleAnalyticsFallback(), 1000);
      }
    } catch (error) {
      console.error('🚫 Google Analytics fallback failed:', error);
    }
  }

  setupGoogleAnalyticsMonitoring() {
    // Monitor Google Analytics loading
    const checkGoogleAnalytics = () => {
      const status = {
        timestamp: new Date().toISOString(),
        gtag: typeof window.gtag !== 'undefined',
        dataLayer: typeof window.dataLayer !== 'undefined' && Array.isArray(window.dataLayer),
        gtagVerified: window.gtagVerified || false,
        gtagLoaded: window.gtagLoaded || false,
        violations: this.googleAnalyticsErrors.length
      };
      
      console.log('📊 Google Analytics Status Check:', status);
      
      if (!status.gtag && this.gtagLoadAttempts < this.maxGtagRetries) {
        console.warn('⚠️ Google Analytics not loaded, scheduling retry...');
        setTimeout(checkGoogleAnalytics, 2000);
      }
    };
    
    // Initial check after page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(checkGoogleAnalytics, 1000);
      });
    } else {
      setTimeout(checkGoogleAnalytics, 1000);
    }
  }

  setupConsoleOverride() {
    // Override console.error to catch network errors
    const originalError = console.error;
    console.error = (...args) => {
      originalError.apply(console, args);
      
      // Check if this is an API error
      if (args.some(arg => 
        typeof arg === 'string' && 
        (arg.includes('API Error') || arg.includes('Network Error'))
      )) {
        this.logAPIError(args);
      }
    };
  }

  logAPIError(errorArgs) {
    const apiError = {
      timestamp: new Date().toISOString(),
      error: errorArgs,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    this.apiTests.push(apiError);
    this.saveAPIErrorsToStorage();
  }

  saveViolationsToStorage() {
    try {
      localStorage.setItem('csp_violations', JSON.stringify(this.violations));
    } catch (e) {
      console.warn('Could not save CSP violations to localStorage:', e);
    }
  }

  saveAPIErrorsToStorage() {
    try {
      localStorage.setItem('api_errors', JSON.stringify(this.apiTests));
    } catch (e) {
      console.warn('Could not save API errors to localStorage:', e);
    }
  }

  async testAPIConnectivity() {
    console.group('🔧 Testing API Connectivity');
    
    try {
      // Test basic connectivity
      const response = await fetch('/api/test/connectivity');
      const data = await response.json();
      
      console.log('✅ Basic API connectivity test:', data);
      
      // Test POST connectivity
      const postResponse = await fetch('/api/test/connectivity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ test: 'connectivity' })
      });
      
      const postData = await postResponse.json();
      console.log('✅ POST API connectivity test:', postData);
      
      // Test CSP debug endpoint
      const cspResponse = await fetch('/api/debug/csp');
      const cspData = await cspResponse.json();
      console.log('✅ CSP debug info:', cspData);
      
      console.groupEnd();
      return true;
      
    } catch (error) {
      console.error('❌ API connectivity test failed:', error);
      console.groupEnd();
      return false;
    }
  }

  async testMenuAPI() {
    console.group('🍕 Testing Menu API');
    
    try {
      // Test categories
      const categoriesResponse = await fetch('/api/menu/categories');
      const categoriesData = await categoriesResponse.json();
      console.log('✅ Categories API test:', categoriesData);
      
      // Test menu items
      const itemsResponse = await fetch('/api/menu/items');
      const itemsData = await itemsResponse.json();
      console.log('✅ Menu items API test:', itemsData);
      
      console.groupEnd();
      return true;
      
    } catch (error) {
      console.error('❌ Menu API test failed:', error);
      console.groupEnd();
      return false;
    }
  }

  getDebugReport() {
    const violations = JSON.parse(localStorage.getItem('csp_violations') || '[]');
    const apiErrors = JSON.parse(localStorage.getItem('api_errors') || '[]');
    
    return {
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      userAgent: navigator.userAgent,
      url: window.location.href,
      cspViolations: violations,
      apiErrors: apiErrors,
      violationCount: violations.length,
      apiErrorCount: apiErrors.length
    };
  }

  clearDebugData() {
    this.violations = [];
    this.apiTests = [];
    localStorage.removeItem('csp_violations');
    localStorage.removeItem('api_errors');
    console.log('🧹 CSP debug data cleared');
  }

  async runFullDiagnostic() {
    console.group('🔍 Running Full CSP/API Diagnostic');
    
    const report = this.getDebugReport();
    console.log('📊 Current debug report:', report);
    
    const connectivityTest = await this.testAPIConnectivity();
    const menuTest = await this.testMenuAPI();
    
    const summary = {
      ...report,
      tests: {
        connectivity: connectivityTest,
        menu: menuTest
      }
    };
    
    console.log('📋 Full diagnostic summary:', summary);
    console.groupEnd();
    
    return summary;
  }
}

// Create global instance
const cspDebugger = new CSPDebugger();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.cspDebugger = cspDebugger;
  window.testAPI = () => cspDebugger.runFullDiagnostic();
  window.clearCSPDebug = () => cspDebugger.clearDebugData();
}

export default cspDebugger;
